import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { BASE_URL, marquesAPI, modelesAPI, piecesAPI, pieceCategoriesAPI } from '../services/api';
import {
    Plus, Trash2, Tag, ChevronRight, Package, Car,
    Search, X, Layers, Save, PlusCircle, Upload
} from 'lucide-react';
import toast from 'react-hot-toast';

// Build logo URL from brand logo filename
const getBrandLogoUrl = (logo) => {
    if (!logo) return null;
    return `${BASE_URL}/car_logos/${logo}`;
};

const VehiculesPage = () => {
    // Data State
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [parts, setParts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState({ brands: false, models: false, parts: false, categories: false });

    // UX State
    const [selectedBrandId, setSelectedBrandId] = useState('');
    const [selectedModel, setSelectedModel] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [showAddModal, setShowAddModal] = useState(null); // 'brand', 'model', 'category'

    // Form State
    const [newPart, setNewPart] = useState({ name: '', price: '', category: '' });
    const [newItem, setNewItem] = useState({ name: '', brand_id: '', year: '' });

    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

    /* ─ API Loaders ─ */
    const loadBrands = useCallback(async () => {
        setLoading(p => ({ ...p, brands: true }));
        try {
            const res = await marquesAPI.list();
            setBrands(res.data.data || []);
        } catch { toast.error('Erreur Marques'); }
        finally { setLoading(p => ({ ...p, brands: false })); }
    }, []);

    const loadCategories = useCallback(async () => {
        setLoading(p => ({ ...p, categories: true }));
        try {
            const res = await pieceCategoriesAPI.list();
            setCategories(res.data.data || []);
        } catch { toast.error('Erreur Catégories'); }
        finally { setLoading(p => ({ ...p, categories: false })); }
    }, []);

    const fetchModels = useCallback(async (targetPage = 1) => {
        setLoading(p => ({ ...p, models: true }));
        try {
            const params = { page: targetPage, limit: 50 };
            if (selectedBrandId) params.brand_id = selectedBrandId;
            if (searchTerm) params.search = searchTerm;

            const res = await modelesAPI.list(params);
            setModels(res.data.data || []);
            if (res.data.pagination) {
                setPagination(res.data.pagination);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(p => ({ ...p, models: false }));
        }
    }, [selectedBrandId, searchTerm]);

    const loadParts = useCallback(async (modelId) => {
        if (!modelId) return;
        setLoading(p => ({ ...p, parts: true }));
        try {
            const res = await piecesAPI.list({ model_id: modelId });
            setParts(res.data.data || []);
        } catch { toast.error('Erreur Pièces'); }
        finally { setLoading(p => ({ ...p, parts: false })); }
    }, []);

    useEffect(() => {
        loadBrands();
        loadCategories();
    }, [loadBrands, loadCategories]);

    // Combined fetch with debounce - always resets to page 1 on filter/search change
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchModels(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, selectedBrandId, fetchModels]);

    /* ─ Actions ─ */
    const openModelDetails = (model) => {
        setSelectedModel(model);
        loadParts(model.id);
        setIsDrawerOpen(true);
    };

    const handleAddBrand = async () => {
        if (!newItem.name.trim()) return;
        try {
            await marquesAPI.create({ name: newItem.name });
            toast.success('Marque ajoutée');
            setNewItem({ name: '', brand_id: '', year: '' });
            setShowAddModal(null);
            loadBrands();
        } catch (err) {
            const msg = err.response?.data?.message || 'Erreur lors de l\'ajout';
            toast.error(msg);
        }
    };

    const handleAddModel = async () => {
        if (!newItem.name.trim() || !newItem.brand_id) {
            toast.error('Nom et Marque requis');
            return;
        }
        try {
            await modelesAPI.create({
                name: newItem.name,
                brand_id: newItem.brand_id,
                year: newItem.year || null
            });
            toast.success('Modèle ajouté');
            setNewItem({ name: '', brand_id: '', year: '' });
            setShowAddModal(null);
            fetchModels();
        } catch { toast.error('Erreur'); }
    };

    const handleAddCategory = async () => {
        if (!newItem.name.trim()) return;
        try {
            await pieceCategoriesAPI.create(newItem.name);
            toast.success('Catégorie ajoutée');
            setNewItem({ name: '', brand_id: '', year: '' });
            setShowAddModal(null);
            loadCategories();
        } catch { toast.error('La catégorie existe déjà ou erreur'); }
    };

    const handleAddPart = async (e) => {
        e.preventDefault();
        if (!newPart.name.trim() || !newPart.category || !selectedModel) {
            toast.error('Désignation et Catégorie obligatoires');
            return;
        }
        try {
            await piecesAPI.create({
                ...newPart,
                model_id: selectedModel.id
            });
            toast.success('Pièce ajoutée');
            setNewPart({ ...newPart, name: '', price: '' }); // Keep category for next add
            loadParts(selectedModel.id);
        } catch { toast.error('Erreur lors de l\'ajout'); }
    };

    const handleDelete = async (type, id) => {
        if (type === 'brand') {
            const brand = brands.find(b => b.id === id);
            const brandModels = models.filter(m => String(m.brand_id) === String(id));
            const modelCount = brandModels.length;
            const msg = modelCount > 0
                ? `⚠️ Supprimer la marque "${brand?.name}"?\n\nCela supprimera aussi ${modelCount} modèle(s) et toutes leurs pièces associées.\n\nCette action est irréversible.`
                : `Supprimer la marque "${brand?.name}" ?`;
            if (!window.confirm(msg)) return;
        } else {
            if (!window.confirm('Confirmer la suppression ?')) return;
        }
        try {
            if (type === 'brand') {
                const res = await marquesAPI.delete(id);
                toast.success(res.data.message || 'Marque supprimée');
                loadBrands();
                fetchModels();
            } else if (type === 'model') {
                await modelesAPI.delete(id);
                setIsDrawerOpen(false);
                fetchModels();
                toast.success('Modèle supprimé');
            } else if (type === 'part') {
                await piecesAPI.delete(id);
                loadParts(selectedModel.id);
                toast.success('Pièce supprimée');
            }
        } catch { toast.error('Erreur suppression'); }
    };

    const handleUploadBrandLogo = async (brandId, file) => {
        const formData = new FormData();
        formData.append('logo', file);
        try {
            await marquesAPI.uploadLogo(brandId, formData);
            toast.success('Logo mis à jour');
            loadBrands();
        } catch { toast.error('Erreur upload logo'); }
    };

    // Helper: get brand info for a model
    const getBrandForModel = (m) => {
        return brands.find(b => String(b.id) === String(m.brand_id));
    };

    return (
        <Layout title="Catalogue Technique">
            <div className="vehicules-pro-explorer">

                {/* 1. Integrated Search & Filter Bar */}
                <div className="pro-control-panel">
                    <div className="search-group">
                        <div className="search-input-wrapper">
                            <Search size={20} className="search-icon" />
                            <input
                                type="text"
                                className="main-search-input"
                                placeholder="Rechercher par modèle ou marque..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && <X size={18} className="clear-icon" onClick={() => setSearchTerm('')} />}
                        </div>

                        <div className="filter-select-wrapper">
                            <Layers size={18} className="filter-icon" />
                            <select
                                className="brand-native-select"
                                value={selectedBrandId}
                                onChange={e => setSelectedBrandId(e.target.value)}
                            >
                                <option value="">Toutes les marques</option>
                                {Array.isArray(brands) && brands.map(b => (
                                    <option key={b.id} value={b.id}>
                                        {b.logo ? '● ' : '○ '}{b.name || b.nom || '—'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="header-actions">
                        <button className="btn btn-primary" onClick={() => setShowAddModal('model')}>
                            <Plus size={16} /> Nouveau Modèle
                        </button>
                        <button className="btn btn-outline" onClick={() => setShowAddModal('brand')} style={{ marginLeft: '8px' }}>
                            <Tag size={16} /> Nouvelle Marque
                        </button>
                    </div>
                </div>

                {/* 2. Responsive Model Grid */}
                <div className="results-container">
                    {loading.models ? (
                        <div className="loading-state">
                            <div className="spinner" />
                            <span>Interrogation de la base de données...</span>
                        </div>
                    ) : (
                        <>
                            <div className="auto-grid">
                                {Array.isArray(models) && models.map(m => {
                                    const brand = getBrandForModel(m);
                                    const brandName = brand?.name || m.brand_name || 'Véhicule';
                                    const logoUrl = getBrandLogoUrl(brand?.logo);
                                    return (
                                        <div key={m.id} className="model-tile" onClick={() => openModelDetails(m)}>
                                            <div className="tile-icon">
                                                {logoUrl ? (
                                                    <img
                                                        src={logoUrl}
                                                        alt={brandName}
                                                        style={{ width: '34px', height: '34px', objectFit: 'contain' }}
                                                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('no-logo'); }}
                                                    />
                                                ) : (
                                                    <Car size={24} />
                                                )}
                                            </div>
                                            <div className="tile-content">
                                                <div className="tile-brand">{brandName}</div>
                                                <div className="tile-name">{m.name || m.nom || '—'}</div>
                                                <div className="tile-year">{m.year || m.annee || 'Année inconnue'}</div>
                                            </div>
                                            <div className="tile-action"><ChevronRight size={18} /></div>
                                        </div>
                                    );
                                })}

                                {(Array.isArray(models) && models.length === 0) && (
                                    <div className="no-result-card">
                                        <Car size={48} strokeWidth={1} style={{ opacity: 0.2, marginBottom: '20px' }} />
                                        <h3>Aucun véhicule trouvé</h3>
                                        <p>Ajustez votre recherche ou ajoutez un nouveau modèle.</p>
                                    </div>
                                )}
                            </div>

                            {/* Pagination Controls */}
                            {pagination.pages > 1 && (
                                <div className="pagination-wrapper" style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginTop: '40px',
                                    padding: '20px',
                                    flexWrap: 'wrap'
                                }}>
                                    <button
                                        className="btn btn-outline btn-sm"
                                        disabled={pagination.page === 1}
                                        onClick={() => fetchModels(pagination.page - 1)}
                                    >
                                        Précédent
                                    </button>

                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                                            // Simple range showing: first 5 or current neighborhood
                                            let pNum = i + 1;
                                            if (pagination.page > 3) pNum = pagination.page - 3 + i;
                                            if (pNum > pagination.pages) return null;
                                            if (pNum < 1) return null;

                                            return (
                                                <button
                                                    key={pNum}
                                                    className={`btn btn-sm ${pagination.page === pNum ? 'btn-primary' : 'btn-ghost'}`}
                                                    onClick={() => fetchModels(pNum)}
                                                    style={{ minWidth: '40px' }}
                                                >
                                                    {pNum}
                                                </button>
                                            );
                                        })}
                                        {pagination.pages > 5 && pagination.page < pagination.pages - 2 && (
                                            <span style={{ alignSelf: 'center', color: 'var(--text-muted)' }}>... {pagination.pages}</span>
                                        )}
                                    </div>

                                    <button
                                        className="btn btn-outline btn-sm"
                                        disabled={pagination.page === pagination.pages}
                                        onClick={() => fetchModels(pagination.page + 1)}
                                    >
                                        Suivant
                                    </button>

                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', width: '100%', textAlign: 'center', marginTop: '8px' }}>
                                        Affichage de {models.length} sur {pagination.total} véhicules
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* 3. Side Drawer for Details & PIECES */}
                {isDrawerOpen && (
                    <>
                        <div className="pro-drawer-overlay" onClick={() => setIsDrawerOpen(false)} />
                        <div className="pro-drawer">
                            <div className="drawer-inner">
                                <div className="drawer-header">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        {(() => {
                                            const brand = brands.find(b => String(b.id) === String(selectedModel?.brand_id));
                                            const logo = brand?.logo;
                                            return (
                                                <div style={{
                                                    width: '100px', height: '100px', borderRadius: '28px',
                                                    background: 'rgba(255,255,255,0.98)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    padding: '14px', flexShrink: 0,
                                                    boxShadow: '0 12px 40px rgba(0,0,0,0.4)'
                                                }}>
                                                    {logo ? (
                                                        <img
                                                            src={getBrandLogoUrl(logo)}
                                                            alt={brand?.name}
                                                            style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                                                        />
                                                    ) : <Car size={52} color="var(--primary-600)" />}
                                                </div>
                                            );
                                        })()}
                                        <div style={{ flex: 1 }}>
                                            <div className="header-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span className="header-subtitle" style={{ fontSize: '14px', fontWeight: '600', opacity: 0.8 }}>Détails Techniques</span>
                                                <button 
                                                    onClick={() => setIsDrawerOpen(false)} 
                                                    style={{ 
                                                        background: 'rgba(255,255,255,0.25)', 
                                                        border: 'none', 
                                                        borderRadius: '50%', 
                                                        width: '32px', 
                                                        height: '32px', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center', 
                                                        color: 'white',
                                                        cursor: 'pointer',
                                                        padding: 0,
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                                                >
                                                    <X size={20} />
                                                </button>
                                            </div>
                                            <h2 className="header-title" style={{ fontSize: '24px', margin: '4px 0', color: 'white' }}>{selectedModel?.name || selectedModel?.nom || '—'}</h2>
                                            <div className="header-tags">
                                                <span className="tag-pill" style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 12px' }}>
                                                    <Layers size={14} /> {selectedModel?.year || 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="drawer-scroller">
                                    {/* --- AJOUTER UNE PIÈCE FORM --- */}
                                    <div className="section-title" style={{ color: 'var(--primary-700)' }}>
                                        <PlusCircle size={16} /> Ajouter une référence
                                    </div>
                                    <form onSubmit={handleAddPart} className="add-part-form">
                                        <div className="form-row-compact">
                                            <input
                                                className="form-control-sm"
                                                placeholder="Désignation"
                                                value={newPart.name}
                                                onChange={e => setNewPart({ ...newPart, name: e.target.value })}
                                                required
                                            />
                                            <div className="currency-input-container">
                                                <input
                                                    className="form-control-sm"
                                                    placeholder="Prix"
                                                    type="number"
                                                    value={newPart.price}
                                                    onChange={e => setNewPart({ ...newPart, price: e.target.value })}
                                                />
                                                <span className="currency-suffix">DZD</span>
                                            </div>
                                        </div>
                                        <div className="form-row-compact" style={{ marginTop: '8px' }}>
                                            <div style={{ flex: 1, position: 'relative', display: 'flex', gap: '4px' }}>
                                                <select
                                                    className="form-control-sm"
                                                    style={{ flex: 1 }}
                                                    value={newPart.category}
                                                    onChange={e => setNewPart({ ...newPart, category: e.target.value })}
                                                    required
                                                >
                                                    <option value="">-- Catégorie --</option>
                                                    {Array.isArray(categories) && categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                                </select>
                                                <button
                                                    type="button"
                                                    className="btn-add-circle"
                                                    title="Nouvelle catégorie"
                                                    onClick={() => setShowAddModal('category')}
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                            <button className="btn btn-primary btn-sm" type="submit">
                                                <Save size={14} /> Enregistrer
                                            </button>
                                        </div>
                                    </form>

                                    <div className="section-divider" />

                                    {/* --- LISTE DES PIÈCES --- */}
                                    <div className="section-title">
                                        <Package size={16} /> Référentiel Pièces ({parts.length})
                                    </div>

                                    {loading.parts ? <div className="spinner" /> : (
                                        <div className="parts-mini-list">
                                            {Array.isArray(parts) && parts.map(p => (
                                                <div key={p.id} className="part-mini-item">
                                                    <div className="part-mini-info">
                                                        <div className="pm-name">{p.name || p.designation || '—'}</div>
                                                        <div className="pm-cat">{p.category || p.category_name || 'Standard'}</div>
                                                    </div>
                                                    <div className="pm-price">{p.price || p.prix_unitaire || 0} DZD</div>
                                                    <button className="pm-delete" onClick={() => handleDelete('part', p.id)}>
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            ))}
                                            {(Array.isArray(parts) && parts.length === 0) && (
                                                <div className="empty-parts-v2">
                                                    <Package size={32} strokeWidth={1} style={{ opacity: 0.2 }} />
                                                    <p>Aucune pièce référencée.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="drawer-footer">
                                    <button className="btn btn-danger btn-sm w-100" onClick={() => handleDelete('model', selectedModel.id)}>
                                        Supprimer ce modèle
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* 4. Modales d'ajout */}
                {showAddModal && (
                    <div className="modal-overlay" onClick={() => setShowAddModal(null)}>
                        <div className="pro-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>
                                    {showAddModal === 'brand' && 'Nouvelle Marque'}
                                    {showAddModal === 'model' && 'Nouveau Modèle'}
                                    {showAddModal === 'category' && 'Nouvelle Catégorie'}
                                </h3>
                                <button className="btn-icon-sm" onClick={() => setShowAddModal(null)}><X size={20} /></button>
                            </div>
                            <div className="modal-body">
                                {showAddModal === 'brand' && (
                                    <div className="form-group">
                                        <label className="form-label">Nom de la marque</label>
                                        <input className="form-control" placeholder="Ex: DACIA" value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} autoFocus />
                                    </div>
                                )}
                                {showAddModal === 'model' && (
                                    <>
                                        <div className="form-group">
                                            <label className="form-label">Marque</label>
                                            <select className="form-control" value={newItem.brand_id} onChange={e => setNewItem({ ...newItem, brand_id: e.target.value })}>
                                                <option value="">Choisir...</option>
                                                {Array.isArray(brands) && brands.map(b => <option key={b.id} value={b.id}>{b.name || b.nom || '—'}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Nom du modèle</label>
                                            <input className="form-control" placeholder="Ex: Sandero Stepway" value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Année</label>
                                            <input className="form-control" type="number" value={newItem.year} onChange={e => setNewItem({ ...newItem, year: e.target.value })} />
                                        </div>
                                    </>
                                )}
                                {showAddModal === 'category' && (
                                    <div className="form-group">
                                        <label className="form-label">Nom de la catégorie de pièce</label>
                                        <input className="form-control" placeholder="Ex: Électricité, Moteur..." value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} autoFocus />
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-ghost" onClick={() => setShowAddModal(null)}>Annuler</button>
                                <button className="btn btn-primary" onClick={
                                    showAddModal === 'brand' ? handleAddBrand :
                                        showAddModal === 'model' ? handleAddModel :
                                            handleAddCategory
                                }>
                                    Confirmer
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 5. Brand Logo Management Section */}
                <div className="card" style={{ marginTop: '20px' }}>
                    <div className="form-section-title" style={{ margin: '0 0 16px 0' }}>🏷️ Logos des Marques</div>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                        Les logos sont automatiquement associés par nom. Vous pouvez uploader un logo personnalisé pour les marques qui n'en ont pas.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                        {Array.isArray(brands) && brands.map(b => (
                            <div key={b.id} style={{
                                background: 'var(--surface-2)', borderRadius: '12px', padding: '12px',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                border: b.logo ? '2px solid var(--primary-100)' : '2px dashed var(--border)',
                                transition: 'all 0.2s', position: 'relative'
                            }}>
                                {/* Delete button */}
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete('brand', b.id); }}
                                    title="Supprimer cette marque"
                                    style={{
                                        position: 'absolute', top: '4px', right: '4px',
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: '#cbd5e1', padding: '2px', borderRadius: '4px',
                                        transition: 'color 0.2s', lineHeight: 1
                                    }}
                                    onMouseEnter={e => e.target.style.color = 'var(--danger)'}
                                    onMouseLeave={e => e.target.style.color = '#cbd5e1'}
                                >
                                    <Trash2 size={12} />
                                </button>
                                {b.logo ? (
                                    <img
                                        src={getBrandLogoUrl(b.logo)}
                                        alt={b.name}
                                        style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                        onError={(e) => { e.target.style.opacity = '0.2'; }}
                                    />
                                ) : (
                                    <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3 }}>
                                        <Car size={28} />
                                    </div>
                                )}
                                <span style={{ fontSize: '10px', fontWeight: '700', textAlign: 'center', color: 'var(--text-primary)' }}>
                                    {b.name || b.nom}
                                </span>
                                <label style={{
                                    fontSize: '9px', color: 'var(--primary-600)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '3px', fontWeight: '700'
                                }}>
                                    <Upload size={10} /> {b.logo ? 'Changer' : 'Ajouter'}
                                    <input type="file" accept="image/*" style={{ display: 'none' }}
                                        onChange={(e) => { if (e.target.files[0]) handleUploadBrandLogo(b.id, e.target.files[0]); }} />
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <style>{`
                .vehicules-pro-explorer { display: flex; flex-direction: column; gap: 20px; max-width: 100%; }
                
                .pro-control-panel {
                    display: flex; justify-content: space-between; align-items: center;
                    background: white; padding: 20px; border-radius: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); gap: 20px; flex-wrap: wrap;
                }

                .search-group { display: flex; gap: 12px; flex: 1; min-width: 300px; }
                .search-input-wrapper {
                    position: relative; flex: 2; min-width: 250px; display: flex; align-items: center;
                    background: #f1f5f9; border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 0 16px;
                    transition: all 0.2s ease;
                    gap: 4px;
                }
                .search-input-wrapper:focus-within {
                    border-color: var(--primary-500);
                    background: white;
                    box-shadow: 0 0 0 4px var(--primary-50);
                }
                .search-icon { 
                    position: static !important;
                    color: #94a3b8; 
                    margin-right: 12px; 
                    flex-shrink: 0; 
                    transition: color 0.2s; 
                }
                .search-input-wrapper:focus-within .search-icon { color: var(--primary-500); }
                
                .main-search-input { 
                    flex: 1; padding: 12px 0; background: transparent; border: none; outline: none; 
                    font-size: 14px; font-weight: 600; color: var(--text-primary);
                    padding-left: 5px;
                }
                .main-search-input::placeholder { color: #94a3b8; font-weight: 500; }
                
                .clear-icon { color: #94a3b8; cursor: pointer; flex-shrink: 0; margin-left: 8px; transition: all 0.2s; }
                .clear-icon:hover { color: var(--danger); transform: scale(1.1); }

                .filter-select-wrapper {
                    position: relative; flex: 1; min-width: 180px; display: flex; align-items: center;
                    background: #f1f5f9; border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 0 12px;
                    transition: all 0.2s ease;
                }
                .filter-select-wrapper:focus-within {
                    border-color: var(--primary-500);
                    background: white;
                    box-shadow: 0 0 0 4px var(--primary-50);
                }
                .filter-icon { color: #94a3b8; margin-right: 10px; flex-shrink: 0; }
                .filter-select-wrapper:focus-within .filter-icon { color: var(--primary-500); }
                .brand-native-select { width: 100%; padding: 12px 0; background: transparent; border: none; outline: none; font-size: 13px; font-weight: 700; cursor: pointer; color: var(--text-primary); }

                .badge-highlight { background: var(--primary-50); color: var(--primary-700); padding: 6px 14px; border-radius: 10px; font-weight: 800; font-size: 12px; display: inline-block; }

                .auto-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
                .model-tile {
                    background: white; border: 1px solid #f1f5f9; border-radius: 16px; padding: 16px;
                    display: flex; align-items: center; gap: 16px; cursor: pointer; transition: all 0.2s;
                }
                .model-tile:hover { box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); transform: translateY(-3px); border-color: var(--primary-300); }
                .tile-icon { width: 50px; height: 50px; background: var(--primary-50); color: var(--primary-600); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden; }
                .tile-icon.no-logo { }
                .tile-brand { font-size: 10px; font-weight: 800; text-transform: uppercase; color: var(--primary-500); }
                .tile-name { font-size: 16px; font-weight: 800; color: var(--text-primary); }
                .tile-year { font-size: 12px; color: var(--text-muted); }

                .pro-drawer {
                    position: fixed; top: 0; right: 0; width: 440px; height: 100%;
                    background: white; z-index: 1001; box-shadow: -10px 0 30px rgba(0,0,0,0.1);
                }
                .drawer-inner { display: flex; flex-direction: column; height: 100%; }
                .drawer-header { padding: 30px 24px; background: #0f172a; color: white; }
                .header-title { font-size: 22px; font-weight: 800; margin: 10px 0; }
                .tag-pill { background: rgba(255,255,255,0.1); padding: 4px 10px; border-radius: 8px; font-size: 11px; font-weight: 700; display: flex; align-items: center; gap: 5px; }

                .drawer-scroller { flex: 1; padding: 24px; overflow-y: auto; }
                .add-part-form { background: white; padding: 18px; border-radius: 16px; margin-bottom: 24px; border: 1px solid var(--primary-100); box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .form-row-compact { display: flex; gap: 10px; align-items: center; }
                .form-control-sm { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 12px; font-size: 13px; outline: none; transition: border 0.2s; }
                .form-control-sm:focus { border-color: var(--primary-400); }
                
                .currency-input-container { position: relative; flex: 0.8; display: flex; align-items: center; }
                .currency-input-container input { width: 100%; padding-right: 45px; }
                .currency-suffix { 
                    position: absolute; right: 12px; font-size: 11px; font-weight: 800; 
                    color: var(--primary-600); background: var(--primary-50); 
                    padding: 2px 6px; border-radius: 6px; pointer-events: none;
                }

                .btn-add-circle { 
                    width: 38px; height: 38px; border-radius: 10px; background: var(--primary-50); 
                    color: var(--primary-600); border: none; cursor: pointer; display: flex; 
                    align-items: center; justify-content: center; transition: all 0.2s;
                }
                .btn-add-circle:hover { background: var(--primary-100); transform: scale(1.05); }

                .section-divider { height: 1px; background: #f1f5f9; margin: 24px 0; }
                .section-title { font-weight: 800; font-size: 14px; margin-bottom: 15px; display: flex; align-items: center; gap: 8px; }

                .part-mini-item { padding: 14px; background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 14px; display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
                .pm-name { font-weight: 700; font-size: 14px; color: var(--text-primary); }
                .pm-cat { font-size: 11px; color: var(--primary-500); font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
                .pm-price { margin-left: auto; font-weight: 800; color: var(--secondary-700); font-size: 13px; background: white; padding: 4px 10px; border-radius: 8px; border: 1px solid #f1f5f9; }
                .pm-delete { color: #cbd5e1; background: none; border: none; cursor: pointer; padding: 6px; transition: color 0.2s; }
                .pm-delete:hover { color: var(--danger); }

                .modal-overlay { position: fixed; top:0; left:0; right:0; bottom:0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(4px); z-index: 2000; display: flex; align-items: center; justify-content: center; }
                .pro-modal { background: white; width: 420px; border-radius: 24px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
                .modal-header { padding: 24px; background: #f8fafc; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .modal-body { padding: 24px; }
                .modal-footer { padding: 20px 24px; background: #f8fafc; display: flex; justify-content: flex-end; gap: 12px; }

                .drawer-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); z-index: 1000; }
                .spinner { width: 24px; height: 24px; border: 3px solid var(--primary-100); border-top-color: var(--primary-600); border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .loading-state { padding: 100px; display: flex; flex-direction: column; align-items: center; gap: 16px; color: var(--text-muted); }
                @media (max-width: 480px) { .pro-drawer { width: 100%; } }
            `}</style>
        </Layout>
    );
};

export default VehiculesPage;
